import { z } from 'zod';

export const PerfArmSchema = z.enum(['base', 'head']);
export type PerfArm = z.infer<typeof PerfArmSchema>;

export const PerfVerdictSchema = z.enum(['faster', 'slower', 'no_change']);
export type PerfVerdict = z.infer<typeof PerfVerdictSchema>;

export const PerfRunStatusSchema = z.enum(['queued', 'running', 'completed', 'failed']);
export type PerfRunStatus = z.infer<typeof PerfRunStatusSchema>;

export const PERF_MIN_EFFECT = 0.1;

export interface PerfRow {
  id: string;
  flavor: 'lighthouse' | 'k6' | 'otel';
  label: string;
  unit: 'ms' | 'rps' | 'ratio' | 'bytes' | 'count';
  gated: boolean;
  base: { median: number; values: number[] };
  head: { median: number; values: number[] };
  /** Ratio of medians; rps flips the sign so positive means worse; ratio rows use percentage-point difference and zero-base rows use absolute difference. */
  deltaPct: number;
  /** Bootstrap confidence interval in the same unit as `deltaPct`. */
  ci95: [number, number];
  verdict: PerfVerdict;
}

export type PerfRunHeadlineDelta = Pick<PerfRow, 'id' | 'label' | 'unit' | 'verdict'> & {
  baseMedian: number;
  deltaPct: number;
  absolute: boolean;
  ci95: [number, number];
};

/** Whether a row's delta and confidence interval use absolute units instead of a ratio. */
export function usesAbsoluteDifference(row: {
  unit: PerfRow['unit'];
  base: { values: number[] };
}): boolean {
  return row.unit === 'ratio' || row.base.values.includes(0);
}

export interface PerfSlowSpan {
  name: string;
  count: number;
  p95Ms: number;
  totalMs: number;
}

export interface PerfResult {
  rounds: number;
  rows: PerfRow[];
  /** Null when neither arm received spans because the app has no OTel instrumentation. */
  otel: null | {
    topSlowSpans: { base: PerfSlowSpan[]; head: PerfSlowSpan[] };
  };
  tools: { lighthouse?: string; k6?: string; otelcol?: string };
}
